// Transfer / Conference / Park controls — works for EVERY call type
// (inbound, manual, predictive, auto). Rendered once inside the Softphone
// (the live-call engine) and driven by window events so any surface
// (ConversationView control bar, softphone control grid) can open it:
//   c7:open-transfer    → opens the Transfer dialog
//   c7:open-conference  → opens the Conference / Park dialog
//   c7:hangup-leg {scope} → selective hangup (customer/third/leave/all)
//
// Backend: routes/me_transfer.py. The agent's active customer leg is resolved
// server-side; for a live manual WebRTC call (no server row) we pass call_ctx
// supplied by the Softphone via getCallCtx().
import { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowRightLeft, Users2, PhoneForwarded, Loader2, UserCheck, Hash,
  ListOrdered, PauseCircle, Phone,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";

// One use per call: once a transfer/conference/park action SUCCEEDS, both the
// Transfer and Conference buttons disable for the rest of the call. A failed
// attempt or a cancelled warm-consult re-enables them.
function markTransferConfUsed() { try { window.dispatchEvent(new CustomEvent("c7:transfer-conf-used")); } catch {} }
function resetTransferConf()   { try { window.dispatchEvent(new CustomEvent("c7:transfer-conf-reset")); } catch {} }
// Agent A has LEFT the call (auto-dispositioned "Transferred"). Tell the
// Softphone to quietly end the live leg and NOT pop a dispose/hangup bar.
function signalTransferNoWrap() { try { window.dispatchEvent(new CustomEvent("c7:transfer-no-wrap")); } catch {} }

export default function CallTransferControls({ dialog, onClose, getCallCtx, onWrapCdr, onConferenceChange }) {
  const [targets, setTargets] = useState({ agents: [], queues: [], agents_available: false });
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [busy, setBusy] = useState(false);

  // Transfer state
  const [xferTab, setXferTab] = useState("agent");
  const [agentId, setAgentId] = useState("");
  const [agentMode, setAgentMode] = useState("blind");   // blind | warm
  const [blindNum, setBlindNum] = useState("");
  const [queueId, setQueueId] = useState("");

  // Conference / park state
  const [confTab, setConfTab] = useState("conference");
  const [confNum, setConfNum] = useState("");
  const [confName, setConfName] = useState("");
  const [parked, setParked] = useState(false);

  // Warm-consult state — when set, a persistent banner offers Complete/Cancel.
  const [consult, setConsult] = useState(null);  // { target:{name,extension}, warm_id }

  const loadTargets = useCallback(async () => {
    setLoadingTargets(true);
    try {
      const r = await api.get("/me/transfer-targets");
      setTargets(r.data || { agents: [], queues: [] });
      if (!(r.data?.agents || []).length) setXferTab("queue");
    } catch (e) { /* non-blocking */ }
    finally { setLoadingTargets(false); }
  }, []);

  // Load targets whenever a dialog opens.
  useEffect(() => { if (dialog) loadTargets(); }, [dialog, loadTargets]);

  // Selective hangup (split hangup button options) — centralized here so the
  // call context + wrap-up dialog routing live in one place.
  useEffect(() => {
    async function onHangupLeg(e) {
      const scope = e?.detail?.scope;
      if (!scope) return;
      try {
        const r = await api.post("/me/active-call/hangup-leg",
          { scope, call_ctx: getCallCtx?.() || null });
        const d = r.data || {};
        if (scope === "customer") toast.success("Customer disconnected — others stay connected");
        if (scope === "third") toast.success("3rd party removed from conference");
        if (scope === "leave" || scope === "all") {
          onConferenceChange?.(false);
          if (d.wrap_cdr) onWrapCdr?.(d.wrap_cdr);
          toast.success(scope === "leave" ? "Left the conference — wrap up the call" : "All lines hung up");
        }
      } catch (err) { toast.error(formatErr(err)); }
    }
    window.addEventListener("c7:hangup-leg", onHangupLeg);
    return () => window.removeEventListener("c7:hangup-leg", onHangupLeg);
  }, [getCallCtx, onWrapCdr, onConferenceChange]);

  function close() { onClose?.(); setBusy(false); setParked(false); }

  async function doTransfer() {
    const callCtx = getCallCtx?.() || null;
    let body = { call_ctx: callCtx };
    if (xferTab === "agent") {
      if (!agentId) return toast.error("Select an agent");
      body = { ...body, transfer_type: "agent", mode: agentMode, target_agent_id: agentId };
    } else if (xferTab === "number") {
      if (!blindNum.trim()) return toast.error("Enter a number");
      body = { ...body, transfer_type: "blind", target_number: blindNum.trim() };
    } else {
      if (!queueId) return toast.error("Select a queue");
      body = { ...body, transfer_type: "queue", target_queue_id: queueId };
    }
    setBusy(true);
    try {
      const r = await api.post("/me/active-call/transfer", body);
      const d = r.data || {};
      if (d.mode === "warm" && d.state === "consulting") {
        toast.success(`Consulting ${d.target?.name || "agent"} — complete or cancel when ready`);
        // surface the warm-complete controls (persistent banner below)
        setConsult({ target: d.target || {}, warm_id: d.warm_id });
        markTransferConfUsed();
        window.dispatchEvent(new CustomEvent("c7:warm-consulting", { detail: d }));
        close();
        return;
      }
      toast.success("Call transferred");
      markTransferConfUsed();
      close();
      // Agent-to-agent transfer: backend auto-dispositions A as "Transferred"
      // and returns skip_wrap → suppress A's dispose/hangup bar entirely.
      if (d.skip_wrap) { signalTransferNoWrap(); return; }
      if (d.wrap_cdr) onWrapCdr?.(d.wrap_cdr);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function doConference() {
    const callCtx = getCallCtx?.() || null;
    if (!confNum.trim()) return toast.error("Enter a number or extension to add");
    setBusy(true);
    try {
      // The conference dialog only has a free-text number/extension field
      // (e.g. "1003" or "+91…"). NEVER send target_agent_id here — a stale
      // agentId from the Transfer dialog's Agent tab would make the backend
      // override the typed number with that agent's extension.
      await api.post("/me/active-call/conference", {
        call_ctx: callCtx,
        target_number: confNum.trim(),
        target_name: confName || undefined,
      });
      toast.success("Added to conference");
      onConferenceChange?.(true);
      markTransferConfUsed();
      close();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function doPark() {
    const callCtx = getCallCtx?.() || null;
    if (!confNum.trim()) return toast.error("Enter a number to dial");
    setBusy(true);
    try {
      await api.post("/me/active-call/park", {
        call_ctx: callCtx, target_number: confNum.trim(), target_name: confName || undefined,
      });
      setParked(true);
      markTransferConfUsed();
      toast.success("Customer parked — dialing the new number");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function doMerge() {
    setBusy(true);
    try {
      await api.post("/me/active-call/park/merge");
      toast.success("Merged into conference");
      onConferenceChange?.(true);
      markTransferConfUsed();
      setParked(false);
      close();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function completeWarm() {
    setBusy(true);
    try {
      const { data } = await api.post("/me/active-call/transfer/complete");
      toast.success("Call transferred");
      markTransferConfUsed();
      setConsult(null);
      onConferenceChange?.(false);
      try { window.dispatchEvent(new CustomEvent("c7:warm-consulting", { detail: { state: "completed" } })); } catch {}
      if (data?.skip_wrap) { signalTransferNoWrap(); return; }
      if (data?.wrap_cdr) onWrapCdr?.(data.wrap_cdr);   // agent A drops → dispose
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function cancelWarm() {
    setBusy(true);
    try {
      await api.post("/me/active-call/transfer/cancel");
      toast.message("Consult cancelled — back with the customer");
      resetTransferConf();
      setConsult(null);
      try { window.dispatchEvent(new CustomEvent("c7:warm-consulting", { detail: { state: "cancelled" } })); } catch {}
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      {/* ───────── Warm-consult banner (persistent until completed/cancelled) ───────── */}
      <Dialog open={!!consult} onOpenChange={() => { /* modal — must Complete or Cancel */ }}>
        <DialogContent className="sm:max-w-md" data-testid="warm-consult-dialog"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-4 h-4" /> Consulting {consult?.target?.name || "agent"}
            </DialogTitle>
            <DialogDescription>
              The customer is on hold. Talk to {consult?.target?.name || `ext ${consult?.target?.extension || ""}`},
              then <b>Complete transfer</b> to hand the customer over (they get the lead pop), or
              <b> Cancel</b> to return to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" disabled={busy}
              onClick={cancelWarm} data-testid="warm-cancel-btn">
              Cancel consult
            </Button>
            <Button className="flex-1" disabled={busy}
              onClick={completeWarm} data-testid="warm-complete-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PhoneForwarded className="w-4 h-4 mr-1.5" />}
              Complete transfer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ───────── Transfer dialog ───────── */}
      <Dialog open={dialog === "transfer"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4" /> Transfer call
            </DialogTitle>
            <DialogDescription>Send this call to an agent, a number, or back to a queue.</DialogDescription>
          </DialogHeader>
          <Tabs value={xferTab} onValueChange={setXferTab}>
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="agent" data-testid="xfer-tab-agent"><UserCheck className="w-3.5 h-3.5 mr-1" />Agent</TabsTrigger>
              <TabsTrigger value="number" data-testid="xfer-tab-number"><Hash className="w-3.5 h-3.5 mr-1" />Number</TabsTrigger>
              <TabsTrigger value="queue" data-testid="xfer-tab-queue"><ListOrdered className="w-3.5 h-3.5 mr-1" />Queue</TabsTrigger>
            </TabsList>

            {/* Agent transfer */}
            <TabsContent value="agent" className="space-y-3 mt-3">
              {loadingTargets ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin" />Loading available agents…</div>
              ) : targets.agents.length === 0 ? (
                <div className="text-sm text-muted-foreground py-2" data-testid="xfer-no-agents">
                  No agents available right now. Use the <button className="underline text-primary" onClick={() => setXferTab("queue")}>Queue</button> tab to transfer this call back to a queue.
                </div>
              ) : (
                <>
                  <Label>Available agents</Label>
                  <ScrollArea className="max-h-40 border rounded-md">
                    <div className="p-1">
                      {targets.agents.map((a) => (
                        <button key={a.id} data-testid={`xfer-agent-${a.id}`}
                          onClick={() => setAgentId(a.id)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between ${agentId === a.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                          <span>{a.name}</span>
                          <span className="text-xs opacity-70">ext {a.extension}</span>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Mode</Label>
                    <div className="flex rounded-md border overflow-hidden text-xs">
                      <button data-testid="xfer-mode-blind" onClick={() => setAgentMode("blind")} className={`px-3 py-1.5 ${agentMode === "blind" ? "bg-primary text-primary-foreground" : "bg-card"}`}>Blind</button>
                      <button data-testid="xfer-mode-warm" onClick={() => setAgentMode("warm")} className={`px-3 py-1.5 ${agentMode === "warm" ? "bg-primary text-primary-foreground" : "bg-card"}`}>Warm (consult)</button>
                    </div>
                  </div>
                  <Button data-testid="xfer-agent-go" onClick={doTransfer} disabled={busy || !agentId} className="w-full">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ArrowRightLeft className="w-4 h-4 mr-1.5" />}
                    {agentMode === "warm" ? "Start consult" : "Transfer now"}
                  </Button>
                </>
              )}
            </TabsContent>

            {/* Blind number */}
            <TabsContent value="number" className="space-y-3 mt-3">
              <div><Label>Customer / external number</Label>
                <Input data-testid="xfer-blind-target" value={blindNum} onChange={(e) => setBlindNum(e.target.value)} placeholder="+91…" /></div>
              <p className="text-xs text-muted-foreground">Blind transfer — you'll drop off and disposition the call; the customer and the new number stay connected.</p>
              <Button data-testid="xfer-blind-go" onClick={doTransfer} disabled={busy || !blindNum.trim()} className="w-full">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PhoneForwarded className="w-4 h-4 mr-1.5" />}Blind transfer
              </Button>
            </TabsContent>

            {/* Queue */}
            <TabsContent value="queue" className="space-y-3 mt-3">
              <div><Label>Transfer back to queue</Label>
                <Select value={queueId} onValueChange={setQueueId}>
                  <SelectTrigger data-testid="xfer-queue-select"><SelectValue placeholder="Select a queue" /></SelectTrigger>
                  <SelectContent>
                    {targets.queues.map((q) => (
                      <SelectItem key={q.id} value={q.id} data-testid={`xfer-queue-${q.id}`}>{q.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
              <Button data-testid="xfer-queue-go" onClick={doTransfer} disabled={busy || !queueId} className="w-full">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ListOrdered className="w-4 h-4 mr-1.5" />}Park to queue
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* ───────── Conference / Park dialog ───────── */}
      <Dialog open={dialog === "conference"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md" data-testid="conference-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users2 className="w-4 h-4" /> Conference / Park</DialogTitle>
            <DialogDescription>Bring a 3rd party in, or park the customer to dial someone first.</DialogDescription>
          </DialogHeader>
          <Tabs value={confTab} onValueChange={(v) => { setConfTab(v); setParked(false); }}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="conference" data-testid="conf-tab-dial"><Users2 className="w-3.5 h-3.5 mr-1" />Dial 3rd party</TabsTrigger>
              <TabsTrigger value="park" data-testid="conf-tab-park"><PauseCircle className="w-3.5 h-3.5 mr-1" />Park &amp; dial</TabsTrigger>
            </TabsList>

            <TabsContent value="conference" className="space-y-3 mt-3">
              <div><Label>Number to add</Label>
                <Input data-testid="conf-target" value={confNum} onChange={(e) => setConfNum(e.target.value)} placeholder="ext 1005 or +91…" /></div>
              <div><Label>Name (optional)</Label>
                <Input data-testid="conf-name" value={confName} onChange={(e) => setConfName(e.target.value)} /></div>
              <Button data-testid="conf-go" onClick={doConference} disabled={busy || !confNum.trim()} className="w-full">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Users2 className="w-4 h-4 mr-1.5" />}Add to conference (3-way)
              </Button>
            </TabsContent>

            <TabsContent value="park" className="space-y-3 mt-3">
              <div><Label>Dial this number (customer parked)</Label>
                <Input data-testid="park-target" value={confNum} onChange={(e) => setConfNum(e.target.value)} placeholder="ext or +91…" disabled={parked} /></div>
              <div><Label>Name (optional)</Label>
                <Input data-testid="park-name" value={confName} onChange={(e) => setConfName(e.target.value)} disabled={parked} /></div>
              {!parked ? (
                <Button data-testid="park-go" onClick={doPark} disabled={busy || !confNum.trim()} className="w-full">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PauseCircle className="w-4 h-4 mr-1.5" />}Park customer &amp; dial
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">Customer parked. Talk to the 3rd party, then merge.</p>
                  <Button data-testid="park-merge" onClick={doMerge} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Phone className="w-4 h-4 mr-1.5" />}Unpark &amp; merge to conference
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
